'use strict'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { produce } from 'immer'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { getMessage } from '../../../../common/utils/MessageUtils'

// --- E10206_05_001–006 (設計書 §8.1)
const E10206_05_001 = getMessage('E010017', 'ファイル種別')
const E10206_05_002 = getMessage('E010017', '項目名')
const E10206_05_003 = getMessage('E010017', '日付形式')
const E10206_05_004 = getMessage('E010001', 'カスタム形式')
const E10206_05_005 = getMessage('E010002', '日付形式')
const E10206_05_006 = getMessage('E10206_003')

// --- 日付形式プリセット。ユーザーが持つ元データの日付形式を定義する
const DATE_FORMAT_PRESETS = [
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
  { label: 'YYYY/MM/DD', value: 'YYYY/MM/DD' },
  { label: 'YYYY年MM月DD日', value: 'YYYY年MM月DD日' },
  { label: 'カスタム', value: '__custom__' }
]

/**
 * DataMapperSetting の日付形式定義と同じ判定ルール。 YYYY / MM / DD の並びと区切り文字を検証する。
 *
 * @param {string} format
 * @returns {{ valid: boolean; message?: string }}
 */
function validateSourceDateFormat(format) {
  if (typeof format !== 'string' || format.trim() === '') {
    return { valid: false, message: E10206_05_005 }
  }
  const input = format.trim()
  const allowedUpper = ['YYYY', 'DD', 'MM'].join('|')
  const allowedLower = ['yyyy', 'dd', 'mm'].join('|')
  const allowedDelimiter = ['.', '\\s', '-', '年', '月', '_', '　', '/'].join('')
  const reg = (allowed) =>
    new RegExp(
      `^(${allowed})([${allowedDelimiter}]?)(?!\\1)(${allowed})([${allowedDelimiter}]?)(?!\\1|\\3)(${allowed})(日?)$`
    )

  const isValidPattern = reg(allowedUpper).test(input) || reg(allowedLower).test(input)
  const hasInvalidJapaneseSuffix = input.endsWith('日') ? !input.includes('年') || !input.includes('月') : false

  return isValidPattern && !hasInvalidJapaneseSuffix ? { valid: true } : { valid: false, message: E10206_05_005 }
}

/**
 * MappingInfo date_format → フォーム用（DB→UI）（SR-104）
 *
 * @param {{
 *   MappingType?: string
 *   SourceDateFormat?: { MappingFileType?: string; MappingColumnName?: string; IsCustom?: boolean; Format?: string }
 * } | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ mappingFileType: string; mappingColumnName: string; dateFormatPreset: string; customFormat: string }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const src = initial?.SourceDateFormat ?? initial?.sourceDateFormat
  if (!src) {
    const firstFile = fileTypes?.[0]
    return {
      mappingFileType: firstFile?.mappingFileType ?? '',
      mappingColumnName: firstFile?.headers?.[0] ?? '',
      dateFormatPreset: DATE_FORMAT_PRESETS[0]?.value ?? 'YYYY-MM-DD',
      customFormat: ''
    }
  }
  const fileType = src.MappingFileType ?? src.mappingFileType ?? ''
  const columnName = src.MappingColumnName ?? src.mappingColumnName ?? ''
  const format = src.Format ?? src.format ?? ''
  const isCustomFlag = src.IsCustom ?? src.isCustom
  const hasPreset = DATE_FORMAT_PRESETS.some((preset) => preset.value !== '__custom__' && preset.value === format)
  // IsCustom が真偽値なら正、なければ legacy データとして preset 一致で推定する。
  const isCustom = typeof isCustomFlag === 'boolean' ? isCustomFlag : Boolean(format) && !hasPreset

  let preset
  if (!format) {
    preset = DATE_FORMAT_PRESETS[0]?.value ?? 'YYYY-MM-DD'
  } else if (isCustom) {
    preset = '__custom__'
  } else {
    preset = hasPreset ? format : '__custom__'
  }

  return {
    mappingFileType: fileType,
    mappingColumnName: columnName,
    dateFormatPreset: preset,
    customFormat: isCustom ? format : ''
  }
}

/**
 * フォーム → MappingInfo date_format（UI→DB）（SR-104）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; dateFormatPreset: string; customFormat: string }} formData
 * @returns {{
 *   MappingType: 'date_format'
 *   SourceDateFormat: { MappingFileType: string; MappingColumnName: string; IsCustom: boolean; Format: string }
 * }}
 */
export function formDataToMappingInfo(formData) {
  const isCustom = formData.dateFormatPreset === '__custom__'
  const format = isCustom ? (formData.customFormat ?? '').trim() : (formData.dateFormatPreset ?? '')
  return {
    MappingType: 'date_format',
    SourceDateFormat: {
      MappingFileType: formData.mappingFileType ?? '',
      MappingColumnName: formData.mappingColumnName ?? '',
      IsCustom: isCustom,
      Format: format
    }
  }
}

/**
 * バリデーション（SR-128, SR-129, SR-130, SR-131）
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; dateFormatPreset: string; customFormat: string }} formData
 * @returns {Record<string, string[]>}
 */
export function validateDateFormatForm(formData) {
  const errors = /** @type {Record<string, string[]>} */ ({})
  if (!(formData.mappingFileType ?? '').trim()) {
    errors.mappingFileType = [E10206_05_001]
  }
  if (!(formData.mappingColumnName ?? '').trim()) {
    errors.mappingColumnName = [E10206_05_002]
  }
  if (!(formData.dateFormatPreset ?? '').trim()) {
    errors.dateFormatPreset = [E10206_05_003]
  }
  if (formData.dateFormatPreset === '__custom__') {
    const custom = (formData.customFormat ?? '').trim()
    if (!custom) {
      errors.customFormat = [E10206_05_004]
    } else {
      const r = validateSourceDateFormat(custom)
      if (!r.valid) {
        errors.customFormat = [r.message ?? E10206_05_005]
      }
    }
  }
  return errors
}

/**
 * 日付形式定義モーダル本体
 *
 * @param {{
 *   initialMappingInfo?: { MappingType?: string; SourceDateFormat?: unknown } | null
 *   fileTypes?: import('../extra/types').FileTypeInfo[]
 *   urlParams?: { dataCreateTaskKey: string; genericKindCode: string }
 *   globalStore?: import('redux').Store
 *   onChange?: (formData: unknown) => void
 *   formData?: Record<string, unknown>
 *   errors?: Record<string, string[]>
 *   getPreview?: (params: unknown, payload: unknown) => Promise<unknown>
 *   previewRows?: unknown[]
 *   onPreviewLoad?: (rows: unknown[]) => void
 *   previewLoading?: boolean
 *   setPreviewLoading?: (v: boolean) => void
 * }} props
 */
export const DateFormatForm = (props) => {
  const {
    initialMappingInfo = null,
    fileTypes = [],
    urlParams = {},
    globalStore,
    onChange,
    formData: externalFormData,
    errors = {},
    getPreview,
    previewRows: externalPreviewRows,
    onPreviewLoad,
    previewLoading: externalPreviewLoading,
    setPreviewLoading
  } = props

  // Use external formData if provided (controlled mode), otherwise use internal (uncontrolled mode)
  const [internalFormData, setInternalFormData] = useState(() => mappingInfoToFormData(initialMappingInfo, fileTypes))

  const isControlled = externalFormData !== undefined
  const formData = isControlled ? externalFormData : internalFormData

  // In uncontrolled mode, notify parent when internal formData changes
  useEffect(() => {
    if (!isControlled) {
      onChange?.(internalFormData)
    }
  }, [internalFormData, onChange, isControlled])

  // In controlled mode, notify parent on first mount
  useEffect(() => {
    if (isControlled) {
      onChange?.(formData)
    }
  }, [])

  const updateForm = useCallback(
    (updater) => {
      const newData = typeof updater === 'function' ? produce(formData, updater) : updater
      if (isControlled) {
        // In controlled mode, notify parent immediately
        onChange?.(newData)
      } else {
        // In uncontrolled mode, update internal state (useEffect will notify parent)
        setInternalFormData(newData)
      }
    },
    [formData, isControlled, onChange]
  )

  const selectedFile = fileTypes?.find((f) => f.mappingFileType === formData.mappingFileType)
  const columnOptions = (selectedFile?.headers ?? []).map((h) => ({ label: h, value: h }))
  const isColumnDisabled = !formData.mappingFileType
  const isCustom = formData.dateFormatPreset === '__custom__'
  const presetOptions = DATE_FORMAT_PRESETS.map((p) => ({
    label: p.label,
    value: p.value
  }))

  const canShowPreview =
    formData.mappingFileType &&
    formData.mappingColumnName &&
    (isCustom ? (formData.customFormat ?? '').trim() && validateSourceDateFormat(formData.customFormat).valid : true)

  return (
    <div className="text-h7">
      <div className="form-table">
        <table className="w-full">
          <tbody>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  ファイル種別 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="date-format-modal-file-type"
                  name="date-format-modal-file-type"
                  value={formData.mappingFileType}
                  onChange={(e) =>
                    updateForm((d) => {
                      d.mappingFileType = e.target.value
                      d.mappingColumnName = ''
                    })
                  }
                  className="w-full"
                  options={(fileTypes ?? []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))}
                  disabled={false}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingFileType?.length)}
                  data-testid="date-format-modal-file-type"
                />
                <ChrofyShowErrors field="mappingFileType" errors={errors} />
              </td>
            </tr>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  項目名 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="date-format-modal-column-name"
                  name="date-format-modal-column-name"
                  value={formData.mappingColumnName}
                  onChange={(e) =>
                    updateForm((d) => {
                      d.mappingColumnName = e.target.value
                    })
                  }
                  className={isColumnDisabled ? 'opacity-60 w-full' : 'w-full'}
                  options={columnOptions}
                  disabled={isColumnDisabled}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingColumnName?.length)}
                  data-testid="date-format-modal-column-name"
                />
                <ChrofyShowErrors field="mappingColumnName" errors={errors} />
              </td>
            </tr>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  元の日付形式 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="date-format-modal-preset"
                  name="date-format-modal-preset"
                  value={formData.dateFormatPreset}
                  onChange={(e) =>
                    updateForm((d) => {
                      d.dateFormatPreset = e.target.value
                      if (e.target.value !== '__custom__') d.customFormat = ''
                    })
                  }
                  className="w-full"
                  options={presetOptions}
                  disabled={false}
                  hasNoSelect={false}
                  isError={Boolean(errors.dateFormatPreset?.length)}
                  data-testid="date-format-modal-preset"
                />
                <ChrofyShowErrors field="dateFormatPreset" errors={errors} />
              </td>
            </tr>
            {isCustom && (
              <tr>
                <th className="form-table__col-head w-[150px]">
                  <label className="text-h7 font-bold" htmlFor="date-format-modal-custom">
                    カスタム形式 <span className="red--text">*</span>
                  </label>
                </th>
                <td className="form-table__col-data">
                  <input
                    id="date-format-modal-custom"
                    type="text"
                    className="text-input w-full text-left"
                    value={formData.customFormat}
                    onChange={(e) =>
                      updateForm((d) => {
                        d.customFormat = e.target.value
                      })
                    }
                    placeholder="例: YYYY-MM-DD"
                    data-testid="date-format-modal-custom-input"
                  />
                  <ChrofyShowErrors field="customFormat" errors={errors} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <p className="text-h8 text-gray-600">
          {canShowPreview
            ? 'フッターの「プレビュー表示」で指定した日付形式の解釈結果を確認できます。'
            : 'ファイル種別・項目名・日付形式を設定するとプレビュー表示が有効になります。'}
        </p>
      </div>
    </div>
  )
}

export default DateFormatForm
